import mixpanel from 'mixpanel-browser'

let initialized = false
let pendingIdentifyId = null
let pendingPeopleProps = null
let pendingPeoplePropsOnce = null

/**
 * Mixpanel must be init'd before identify/people.set. Partner GraphQL can finish
 * after CURRENT_USER / Site effects, so callers should use these helpers to queue
 * until init runs (avoids blank dashboard from throws in Apollo onCompleted).
 */
export function initMixpanel(token, options = {}) {
  if (initialized || !token) return

  mixpanel.init(token, options)
  initialized = true

  if (pendingIdentifyId != null) {
    mixpanel.identify(pendingIdentifyId)
    pendingIdentifyId = null
  }
  if (pendingPeopleProps != null) {
    mixpanel.people?.set(pendingPeopleProps)
    pendingPeopleProps = null
  }
  if (pendingPeoplePropsOnce != null) {
    mixpanel.people?.setOnce(pendingPeoplePropsOnce)
    pendingPeoplePropsOnce = null
  }
}

export function identifyMixpanel(id) {
  if (id == null || id === '') return

  if (!initialized) {
    pendingIdentifyId = id
    return
  }

  try {
    mixpanel.identify(id)
  } catch (err) {
    console.warn('mixpanel.identify failed', err)
  }
}

export function setMixpanelPeople(props) {
  if (!props) return

  if (!initialized) {
    pendingPeopleProps = { ...(pendingPeopleProps || {}), ...props }
    return
  }

  try {
    mixpanel.people?.set(props)
  } catch (err) {
    console.warn('mixpanel.people.set failed', err)
  }
}

export function setMixpanelPeopleOnce(props) {
  if (!props) return

  // TODO(MM-3453): remove debug logs after QA
  console.log('[MM-3453] setMixpanelPeopleOnce', props, { initialized })

  if (!initialized) {
    pendingPeoplePropsOnce = { ...(pendingPeoplePropsOnce || {}), ...props }
    return
  }

  try {
    mixpanel.people?.setOnce(props)
  } catch (err) {
    console.warn('mixpanel.people.setOnce failed', err)
  }
}

/**
 * One canonical signup event with source, partner, and trial/plan props.
 * @param {Object} [opts]
 * @param {string|number} [opts.userId]
 * @param {string} [opts.signupSource]
 * @param {string} [opts.partner]
 * @param {string|Date} [opts.trialExpiresAt]
 * @param {string} [opts.pricingPlan]
 * @param {boolean} [opts.onTrial]
 */
export function trackSignupCompleted(opts = {}) {
  const {
    userId,
    signupSource,
    partner,
    trialExpiresAt,
    pricingPlan,
    onTrial,
  } = opts

  if (userId != null && userId !== '') {
    identifyMixpanel(String(userId))
  }

  const props = {
    signup_source: signupSource,
    partner: partner || window.parameterizedPartnerName || window.partnerName,
    on_trial: onTrial ?? !!trialExpiresAt,
  }
  if (trialExpiresAt != null && trialExpiresAt !== '') {
    props.trial_expires_at = trialExpiresAt
  }
  if (pricingPlan != null && pricingPlan !== '') {
    props.pricing_plan = pricingPlan
  }

  // TODO(MM-3453): remove debug logs after QA
  console.log('[MM-3453] trackSignupCompleted', { userId, props })

  window.track_mixpanel?.('CompleteRegistration', props)
}

const FIRST_ACTIVATION_TRACKING_SINCE = '2026-10-14T00:00:00.000Z'

/**
 * @param {Object} [opts]
 * @param {string} [opts.campaignType]
 * @param {string|Date} [opts.userCreatedAt]  
 */
export function trackCampaignActivated(opts = {}) {
  const { campaignType, userCreatedAt } = opts
  if (!campaignType) return

  const createdAt = userCreatedAt || window.userCreatedAt
  const createdMs = createdAt ? new Date(createdAt).getTime() : NaN
  const sinceMs = new Date(FIRST_ACTIVATION_TRACKING_SINCE).getTime()
  const eligibleForFirstActivation =
    !Number.isNaN(createdMs) && !Number.isNaN(sinceMs) && createdMs >= sinceMs

  // TODO(MM-3453): remove debug logs after QA
  console.log('[MM-3453] trackCampaignActivated', {
    campaignType,
    createdAt,
    cutoff: FIRST_ACTIVATION_TRACKING_SINCE,
    eligibleForFirstActivation,
  })

  window.track_mixpanel?.('campaign:activated', { campaign_type: campaignType })

  // Old users (signup before cutoff): event only, skip first-activation people props
  if (!eligibleForFirstActivation) return

  setMixpanelPeopleOnce({
    first_activation_at: new Date().toISOString(),
    first_activation_campaign_type: campaignType,
  })
}
